import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import appModeService from 'services/AppModeService';

/**
 * App Mode Context
 * 
 * Provides app mode (embedded/standalone) and token management
 * to all components via React Context API.
 */

// Create context
const AppModeContext = createContext(null);

/**
 * AppModeProvider Component
 * 
 * Wraps the app and provides app mode state and service instance
 * to all child components.
 */
export const AppModeProvider = ({ children }) => {
  // Local state to trigger re-renders when service state changes
  const [state, setState] = useState(() => appModeService.getState());

  useEffect(() => {
    // Subscribe to service updates
    const unsubscribe = appModeService.subscribe(() => {
      // When service state changes, update React state to trigger re-render
      setState(appModeService.getState());
    });

    // Cleanup on unmount
    return unsubscribe;
  }, []);

  // Wrapped methods for easier use (memoized with useCallback)
  const getToken = useCallback(() => {
    return appModeService.getToken();
  }, []);

  const requestToken = useCallback(() => {
    appModeService.requestToken();
  }, []);

  const clearToken = useCallback(() => {
    appModeService.clearToken();
  }, []);

  // Context value
  const value = {
    // Service instance (for advanced usage)
    service: appModeService,

    // State values
    mode: state.mode,
    token: state.token,
    isTokenReady: state.isTokenReady,
    isEmbedded: state.isEmbedded,
    isStandalone: state.isStandalone,
    error: state.error,

    // Methods
    getToken,
    requestToken,
    clearToken,
  };

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>;
};

/**
 * useAppMode Hook
 * 
 * Custom hook to access app mode state and methods.
 * Must be used within AppModeProvider.
 * 
 * @returns {Object} App mode state and methods
 * @throws {Error} If used outside AppModeProvider
 * 
 * @example
 * const { isEmbedded, token, isTokenReady } = useAppMode();
 */
export const useAppMode = () => {
  const context = useContext(AppModeContext);

  if (!context) {
    throw new Error('useAppMode must be used within AppModeProvider');
  }

  return context;
};

